import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';
import { usePopper } from 'react-popper';
import * as popper from '@popperjs/core';
import maxSizeModifier from 'popper-max-size-modifier';
import { applyMaxSizeModifier, getTooltipOffset, areElementsIntersecting } from './utils';
import { useTheme } from '../theme';

export type TooltipProps = {
  content: React.ReactNode;
  children: React.ReactNode;
  placement?: popper.Placement;
  parentPortal?: HTMLElement;
  size?: 'small' | 'medium' | 'large';
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  triggerProps?: React.HTMLAttributes<HTMLSpanElement>;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'content'>;

const classNamePrefix = `airtame-tooltip`;

/**
 * Tooltips display informative text when users hover over a trigger element.
 *
 * Example:
 * ```ts
 * <Tooltip content={<div>Tooltip content</div>} placement="top">
 *  <button>Trigger</button>
 * </Tooltip>
 * ```
 */
export const Tooltip = ({
  children,
  content,
  className,
  placement = 'top',
  parentPortal = document.body,
  size,
  open,
  onOpen,
  onClose,
  triggerProps,
  ...props
}: TooltipProps): React.ReactElement => {
  const theme = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [triggerElement, setTriggerElement] = useState<HTMLSpanElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const [bridgeElement, setBridgeElement] = useState<HTMLDivElement | null>(null);
  const { styles, attributes, update } = usePopper(triggerElement, popperElement, {
    placement,
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } },
      {
        name: 'offset',
        options: {
          offset: getTooltipOffset(placement),
        },
      },
      maxSizeModifier,
      applyMaxSizeModifier,
    ],
  });

  const classNames = classnames(classNamePrefix, className, {
    [`${classNamePrefix}--visible`]: isVisible,
    [`${classNamePrefix}--size-${size ?? ''}`]: size,
  });

  useEffect(() => {
    if (open === undefined) {
      return;
    }

    // NOTE: Guard the use of the component when the props 'open' is defined
    if (onOpen === undefined || onClose === undefined) {
      throw new Error(
        `Tooltip component requires to have "onOpen" and "onClose" props defined
        when "open" prop is defined`
      );
    }
    if (open) {
      setIsVisible(true);
      setIsMounted(true);
    } else {
      setIsVisible(false);
    }
  }, [open]);

  const handleOpen = () => {
    if (!isVisible) {
      // We have to force an update manually because sometimes the tooltip is
      // generated in a drawer or an hidden tab getting the wrong positioning.
      // The update function can be null:
      // https://popper.js.org/react-popper/v2/hook/#update-forceupdate-and-state
      const updatePromise = update ?? (() => Promise.resolve());
      updatePromise()
        .then(() => {
          setIsVisible(true);
          setIsMounted(true);
          onOpen?.();
        })
        .catch(() => {
          // noop
        });
    }
  };

  const handleClose = (e: React.PointerEvent) => {
    const exitElement = e.relatedTarget;
    // On mouseLeave we don't want to close the tooltip if the exitElement
    // is the triggerElement or the bridgeElement
    if (
      areElementsIntersecting(triggerElement, exitElement) ||
      areElementsIntersecting(bridgeElement, exitElement)
    ) {
      return;
    }
    setIsVisible(false);
  };

  const handleUnmount = (e: React.AnimationEvent) => {
    if (e.animationName === 'tooltip-close') {
      setIsMounted(false);
      onClose?.();
    }
  };

  return (
    <span
      {...triggerProps}
      ref={setTriggerElement}
      onPointerEnter={handleOpen}
      onPointerLeave={handleClose}
    >
      {children}
      {isMounted &&
        createPortal(
          <div
            {...props}
            {...attributes.popper}
            style={styles.popper}
            className={classNames}
            ref={setPopperElement}
            onPointerLeave={handleClose}
            onAnimationEnd={handleUnmount}
            data-theme={theme}
          >
            <div className="airtame-tooltip__arrow" ref={setArrowElement} style={styles.arrow} />
            <div className="airtame-tooltip__arrow-bridge" ref={setBridgeElement} />
            {content}
          </div>,
          parentPortal
        )}
    </span>
  );
};

Tooltip.displayName = 'Tooltip';
